/**
 * Show a toast notification
 * @param {string} message - Message to display
 * @param {string} type - Toast type: 'success' | 'error' | 'info'
 */
export function showToast(message, type = 'success') {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const toast = document.createElement('div');
  const bgColor = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-blue-600',
  }[type] || 'bg-gray-800';

  toast.className = `toast px-4 py-2 ${bgColor} text-white rounded-lg shadow-lg text-sm font-medium`;
  toast.textContent = message;

  container.appendChild(toast);

  // Remove toast after animation
  setTimeout(() => {
    toast.remove();
  }, 3000);
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} True if successful
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Fallback for older browsers
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (fallbackError) {
      console.error('Copy failed:', fallbackError);
      return false;
    }
  }
}

/**
 * Share a link using native share sheet or fallback to clipboard
 * @param {string} url - URL to share
 * @param {string} title - Title for the share
 * @param {string} text - Description text
 * @returns {Promise<boolean>} True if shared/copied successfully
 */
export async function shareLink(url, title, text) {
  // Use native share sheet if available (mobile)
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url,
      });
      return true;
    } catch (e) {
      if (e.name === 'AbortError') {
        // User cancelled share
        return false;
      }
      console.error('Share failed:', e);
      // Fall through to clipboard
    }
  }

  // Fallback: copy to clipboard
  const success = await copyToClipboard(url);
  if (success) {
    showToast('Link copied to clipboard!');
  }
  return success;
}

/**
 * Check if native share is available
 * @returns {boolean}
 */
export function canNativeShare() {
  return Boolean(navigator.share);
}
